import type { Disposable } from "@/shared/contracts/lifecycle";
import { getMessages } from "@/i18n/runtime";
import { renderBasicsPanel } from "./basicsPanel";
import { renderHovPanel } from "./hovPanel";
import { renderTimPanel } from "./timPanel";
import type { HelpTopicId } from "./helpTopics";

interface HelpViewOptions {
  readonly container: HTMLElement;
  readonly isTouchPrimary: boolean;
  readonly isTimHovActivationUnlocked: boolean;
  readonly isMHovUnlocked: boolean;
  readonly isNHovUnlocked: boolean;
  readonly isMHovHelpRead: boolean;
  readonly isMHovHelpUnread: boolean;
  readonly onHelpTopicRead: (topicId: HelpTopicId) => void;
}

type HelpTabId = "basics" | "hov" | "tim";

export class HelpView implements Disposable {
  private readonly root: HTMLDivElement;
  private readonly tabs: ReadonlyMap<HelpTabId, HTMLButtonElement>;
  private readonly panels: ReadonlyMap<HelpTabId, HTMLDivElement>;
  private readonly hovContent: HTMLDivElement;
  private readonly onHelpTopicRead: (topicId: HelpTopicId) => void;
  private isMHovUnlocked: boolean;
  private isNHovUnlocked: boolean;
  private isMHovHelpRead: boolean;
  private activeTab: HelpTabId = "basics";
  private readonly cleanups: Array<() => void> = [];

  constructor(options: HelpViewOptions) {
    const messages = getMessages();
    this.onHelpTopicRead = options.onHelpTopicRead;
    this.isMHovUnlocked = options.isMHovUnlocked;
    this.isNHovUnlocked = options.isNHovUnlocked;
    this.isMHovHelpRead = options.isMHovHelpRead;

    this.root = document.createElement("div");
    this.root.className = "hover-idle-help";

    const title = document.createElement("h1");
    title.className = "hover-idle-help-title";
    title.textContent = messages.navigation.items.help.screenLabel;

    const tabBar = document.createElement("div");
    tabBar.className = "hover-idle-core-tabs";
    tabBar.setAttribute("role", "tablist");
    tabBar.setAttribute("aria-label", messages.help.tabsAria);

    const tabDefs: ReadonlyArray<{
      readonly id: HelpTabId;
      readonly label: string;
    }> = [
      { id: "basics", label: messages.help.tabs.basics },
      { id: "hov", label: messages.help.tabs.hov },
      { id: "tim", label: messages.help.tabs.tim },
    ];

    const tabs = new Map<HelpTabId, HTMLButtonElement>();
    const panels = new Map<HelpTabId, HTMLDivElement>();

    for (const def of tabDefs) {
      const tabButton = document.createElement("button");
      tabButton.type = "button";
      tabButton.className = "hover-idle-core-tab hover-idle-help-tab";
      tabButton.textContent = def.label;
      tabButton.dataset.tab = def.id;
      tabButton.dataset.active = def.id === this.activeTab ? "true" : "false";
      tabButton.dataset.unseen =
        def.id === "hov" && options.isMHovHelpUnread ? "true" : "false";
      tabButton.setAttribute("role", "tab");
      tabButton.setAttribute(
        "aria-selected",
        def.id === this.activeTab ? "true" : "false",
      );
      if (def.id === "hov" && options.isMHovHelpUnread) {
        const unreadLabel = messages.help.unreadLabel;
        tabButton.title = unreadLabel;
        tabButton.setAttribute("aria-label", `${def.label}: ${unreadLabel}`);
      }

      const handler = (): void => this.switchTab(def.id);
      this.addTabIntentListeners(tabButton, handler);
      this.cleanups.push(() => {
        this.removeTabIntentListeners(tabButton, handler);
      });

      tabBar.append(tabButton);
      tabs.set(def.id, tabButton);
    }

    this.tabs = tabs;

    const basicsPanel = document.createElement("div");
    basicsPanel.className = "hover-idle-help-panel";
    basicsPanel.dataset.tab = "basics";
    basicsPanel.setAttribute("role", "tabpanel");
    const basicsContent = document.createElement("div");
    basicsContent.className =
      "hover-idle-help-content hover-idle-basics-content";
    renderBasicsPanel(basicsContent, options.isTouchPrimary, messages);
    basicsPanel.append(
      this.createTabSection("basics", messages.help.tabs.basics, basicsContent),
    );
    panels.set("basics", basicsPanel);

    const hovPanel = document.createElement("div");
    hovPanel.className = "hover-idle-help-panel";
    hovPanel.dataset.tab = "hov";
    hovPanel.setAttribute("role", "tabpanel");
    hovPanel.hidden = true;
    const hovContent = document.createElement("div");
    hovContent.className = "hover-idle-help-content hover-idle-hov-content";
    renderHovPanel(hovContent, messages, {
      isMHovUnlocked: options.isMHovUnlocked,
      isNHovUnlocked: options.isNHovUnlocked,
    });
    this.hovContent = hovContent;
    hovPanel.append(
      this.createTabSection("hov", messages.help.tabs.hov, hovContent),
    );
    panels.set("hov", hovPanel);

    const timPanel = document.createElement("div");
    timPanel.className = "hover-idle-help-panel";
    timPanel.dataset.tab = "tim";
    timPanel.setAttribute("role", "tabpanel");
    timPanel.hidden = true;
    const timContent = document.createElement("div");
    timContent.className = "hover-idle-help-content hover-idle-tim-content";
    renderTimPanel(timContent, messages, {
      isTouchPrimary: options.isTouchPrimary,
      isTimHovActivationUnlocked: options.isTimHovActivationUnlocked,
    });
    timPanel.append(
      this.createTabSection("tim", messages.help.tabs.tim, timContent),
    );
    panels.set("tim", timPanel);

    this.panels = panels;

    this.root.append(tabBar, title, basicsPanel, hovPanel, timPanel);
    options.container.replaceChildren(this.root);
  }

  dispose(): void {
    for (const cleanup of this.cleanups) cleanup();
    this.cleanups.length = 0;
    this.root.remove();
  }

  updateHelpNotificationState(options: {
    readonly isMHovUnlocked: boolean;
    readonly isNHovUnlocked: boolean;
    readonly isMHovHelpRead: boolean;
    readonly isMHovHelpUnread: boolean;
  }): void {
    this.isMHovHelpRead = options.isMHovHelpRead;
    if (
      this.isMHovUnlocked !== options.isMHovUnlocked ||
      this.isNHovUnlocked !== options.isNHovUnlocked
    ) {
      this.isMHovUnlocked = options.isMHovUnlocked;
      this.isNHovUnlocked = options.isNHovUnlocked;
      renderHovPanel(this.hovContent, getMessages(), {
        isMHovUnlocked: options.isMHovUnlocked,
        isNHovUnlocked: options.isNHovUnlocked,
      });
    }
    this.syncHovTabUnread(options.isMHovHelpUnread);
  }

  private addTabIntentListeners(
    button: HTMLButtonElement,
    handler: () => void,
  ): void {
    button.addEventListener("pointerenter", handler);
    button.addEventListener("focus", handler);
    button.addEventListener("click", handler);
  }

  private removeTabIntentListeners(
    button: HTMLButtonElement,
    handler: () => void,
  ): void {
    button.removeEventListener("pointerenter", handler);
    button.removeEventListener("focus", handler);
    button.removeEventListener("click", handler);
  }

  private switchTab(id: HelpTabId): void {
    if (id === this.activeTab) return;
    this.panels.get(this.activeTab)!.hidden = true;
    this.panels.get(id)!.hidden = false;
    for (const [tabId, button] of this.tabs) {
      const isActive = tabId === id;
      button.dataset.active = isActive ? "true" : "false";
      button.setAttribute("aria-selected", isActive ? "true" : "false");
    }
    this.activeTab = id;
    if (id === "hov" && this.isMHovUnlocked && !this.isMHovHelpRead) {
      this.isMHovHelpRead = true;
      this.onHelpTopicRead("mhov");
      this.syncHovTabUnread(false);
    }
  }

  private syncHovTabUnread(isUnread: boolean): void {
    const button = this.tabs.get("hov");
    if (button === undefined) return;
    const messages = getMessages();
    button.dataset.unseen = isUnread ? "true" : "false";
    if (isUnread) {
      const unreadLabel = messages.help.unreadLabel;
      button.title = unreadLabel;
      button.setAttribute(
        "aria-label",
        `${messages.help.tabs.hov}: ${unreadLabel}`,
      );
      return;
    }
    button.title = "";
    button.setAttribute("aria-label", messages.help.tabs.hov);
  }

  private createTabSection(
    tabId: HelpTabId,
    title: string,
    ...children: HTMLElement[]
  ): HTMLDivElement {
    const section = document.createElement("div");
    section.className = "hover-idle-help-section";
    section.dataset.tab = tabId;

    const header = document.createElement("div");
    header.className = "hover-idle-help-section-header";

    const heading = document.createElement("h2");
    heading.className = "hover-idle-help-section-title";
    heading.textContent = title;

    header.append(heading);
    section.append(header, ...children);
    return section;
  }
}
